import {
  isConfirmationRequest,
  type ChatContentBlock,
  type ChatTextBlock,
  type ChatToolResultBlock,
  type ChatToolUseBlock,
  type ChatTurnResponse,
  type ConfirmationRequestContent,
} from '../../types';

export interface AssistantTextItem {
  kind: 'text';
  text: string;
}

export interface AssistantToolItem {
  kind: 'tool';
  id: string;
  /** Raw tool name from the Anthropic ToolUseBlock (e.g. "get_field_distribution").
   *  Stable identifier for logs and state keying. Use `displayName` for UI. */
  name: string;
  /** User-facing label sourced from the BE — falls back to `name` for orphan
   *  tool_use blocks (shouldn't happen in practice but kept defensive). */
  displayName: string;
  input: unknown;
  result?: {
    summary: string;
    isError: boolean;
    /** Structured tool output (SC-2157). Carries `needs_confirmation` discriminator
     *  for write-tool confirmation round-trips; read tools carry their structured
     *  output. */
    content?: Record<string, unknown> | string;
  };
  /** Set when `result.content` is a ConfirmationRequest. Drives the Confirm/Cancel
   *  footer in ToolPill. */
  confirmation?: ConfirmationRequestContent;
  /** True when a later turn invoked the same write tool with `confirmed=true` and
   *  matching `finding_id` — meaning the confirmation has already been resolved
   *  in history. Suppresses the Confirm/Cancel footer on replay. */
  confirmationResolved?: boolean;
}

export type AssistantItem = AssistantTextItem | AssistantToolItem;

export interface UserVisualGroup {
  kind: 'user';
  turnId: string;
  blocks: ChatTextBlock[];
}

export interface AssistantVisualGroup {
  kind: 'assistant';
  turnIds: string[];
  items: AssistantItem[];
}

export type VisualGroup = UserVisualGroup | AssistantVisualGroup;

function isVisible(block: ChatContentBlock): boolean {
  // `canvas_context` (SC-2853) is the frozen model-facing snapshot render that
  // rides a user turn — protocol bookkeeping like thinking, never a bubble.
  return (
    block.type !== 'thinking' &&
    block.type !== 'redacted_thinking' &&
    block.type !== 'canvas_context'
  );
}

function buildToolResultMap(
  turns: ChatTurnResponse[],
): Map<string, ChatToolResultBlock> {
  const map = new Map<string, ChatToolResultBlock>();
  for (const turn of turns) {
    for (const block of turn.content) {
      if (block.type === 'tool_result') {
        map.set(block.tool_use_id, block);
      }
    }
  }
  return map;
}

/**
 * Set of finding_ids that have already been confirmed (a downstream tool_use
 * was invoked with `confirmed=true` for that id). Used to suppress the
 * Confirm/Cancel footer when re-rendering history past the resolution.
 *
 * Generic over tool name — any write tool following the SC-2157 confirmation
 * pattern keys its proposed_action by `finding_id` per the BE wire contract.
 */
function buildResolvedFindingIds(turns: ChatTurnResponse[]): Set<string> {
  const resolved = new Set<string>();
  for (const turn of turns) {
    if (turn.role !== 'assistant') continue;
    for (const block of turn.content) {
      if (block.type !== 'tool_use') continue;
      const findingId = extractConfirmedFindingId(block);
      if (findingId) resolved.add(findingId);
    }
  }
  return resolved;
}

function extractConfirmedFindingId(block: ChatToolUseBlock): string | null {
  return findingIdFromConfirmedInput(block.input);
}

function findingIdFromConfirmedInput(input: unknown): string | null {
  if (typeof input !== 'object' || input === null) return null;
  const inp = input as { confirmed?: unknown; finding_id?: unknown };
  if (inp.confirmed !== true) return null;
  if (typeof inp.finding_id !== 'string' || inp.finding_id.length === 0) return null;
  return inp.finding_id;
}

/**
 * Cross-source confirmation resolution. `groupTurns` only sees the replayed
 * history; once the user confirms, the second `apply_finding` tool_use lives
 * in `localGroups` or `inFlight` until the next replay refetch. Without this
 * post-pass, the original confirmation card keeps showing Confirm/Cancel even
 * after the agent has already applied the finding.
 *
 * Walks every assistant tool item across the merged group list, collects
 * finding_ids that have a `confirmed=true` invocation anywhere, and flips
 * `confirmationResolved` on items whose pending confirmation matches.
 */
export function markResolvedConfirmations(groups: VisualGroup[]): VisualGroup[] {
  const resolved = new Set<string>();
  for (const group of groups) {
    if (group.kind !== 'assistant') continue;
    for (const item of group.items) {
      if (item.kind !== 'tool') continue;
      const findingId = findingIdFromConfirmedInput(item.input);
      if (findingId) resolved.add(findingId);
    }
  }

  if (resolved.size === 0) return groups;

  return groups.map((group) => {
    if (group.kind !== 'assistant') return group;
    let mutated = false;
    const items = group.items.map((item) => {
      if (item.kind !== 'tool' || !item.confirmation) return item;
      const fid = item.confirmation.proposed_action.finding_id;
      if (resolved.has(fid) && !item.confirmationResolved) {
        mutated = true;
        return { ...item, confirmationResolved: true };
      }
      return item;
    });
    return mutated ? { ...group, items } : group;
  });
}

/**
 * Collapses raw chat turns into renderable visual groups per the SC-1612 brief:
 *   1. Strip thinking / redacted_thinking / canvas_context blocks.
 *   2. Skip turns with no remaining blocks.
 *   3. Skip user turns whose remaining blocks are all tool_result (protocol bookkeeping).
 *   4. Coalesce consecutive assistant turns (separated only by skipped user turns) into one group.
 *   5. Tool-use blocks resolve their summary by tool_use_id from any tool_result block in the session.
 */
export function groupTurns(turns: ChatTurnResponse[]): VisualGroup[] {
  const toolResults = buildToolResultMap(turns);
  const resolvedFindingIds = buildResolvedFindingIds(turns);
  const groups: VisualGroup[] = [];
  let openAssistant: AssistantVisualGroup | null = null;

  const closeAssistant = () => {
    if (openAssistant) groups.push(openAssistant);
    openAssistant = null;
  };

  for (const turn of turns) {
    const visible = turn.content.filter(isVisible);
    if (visible.length === 0) continue;

    if (turn.role === 'user') {
      const textBlocks = visible.filter(
        (b): b is ChatTextBlock => b.type === 'text',
      );
      if (textBlocks.length === 0) continue;
      closeAssistant();
      groups.push({ kind: 'user', turnId: turn.id, blocks: textBlocks });
      continue;
    }

    const newItems: AssistantItem[] = [];
    for (const block of visible) {
      if (block.type === 'text') {
        newItems.push({ kind: 'text', text: block.text });
      } else if (block.type === 'tool_use') {
        const result = toolResults.get(block.id);
        const confirmation = isConfirmationRequest(result?.content)
          ? result.content
          : undefined;
        const confirmationResolved =
          confirmation !== undefined &&
          resolvedFindingIds.has(confirmation.proposed_action.finding_id);
        newItems.push({
          kind: 'tool',
          id: block.id,
          name: block.name,
          // BE's display label lives on the tool_result block. Fall back to
          // the raw tool name only if the result hasn't landed yet (in-flight
          // edge case).
          displayName: result?.display_name ?? block.name,
          input: block.input,
          result: result
            ? {
                summary: result.summary,
                isError: result.is_error,
                content: result.content,
              }
            : undefined,
          confirmation,
          confirmationResolved,
        });
      }
    }
    if (newItems.length === 0) continue;

    if (!openAssistant) {
      openAssistant = { kind: 'assistant', turnIds: [], items: [] };
    }
    openAssistant.turnIds.push(turn.id);
    openAssistant.items.push(...newItems);
  }
  closeAssistant();
  return groups;
}
